/** @jsxImportSource @emotion/react */
"use client";
import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { useFormatter, useTranslations } from "@i18n/react";
import type { TheWordGame } from "@packages/sdk";
import type { JSX } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { ButtonProps } from "../../_core/Button/Button.emotion";
import { Text } from "../../_core/Text/Text.emotion";

const hallowElement: HallowElement = "div";

const actionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
  zIndex: 1,
};
const containerStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  width: spacing.fit,
  [cssMediaQueryMinWidthTablet]: {
    gap: spacing.l,
  },
};
const iconContainerStyle: FunctionInterpolation<{ size: number }> = (
  props,
) => ({
  alignItems: "center",
  borderRadius: radius.full,
  display: "flex",
  height: props.size,
  justifyContent: "center",
  minHeight: props.size,
  minWidth: props.size,
  width: props.size,
});
const iconContainerSuccessStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.wordSuccess,
  borderRadius: radius.xs,
});
const iconContainerPartialStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.wordPartial,
});
const iconContainerNeverStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.wordNever,
});
const guessContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  gap: spacing.t,
  justifyContent: "center",
};
const guessesContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
  justifyContent: "center",
};
const summaryContainerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "space-around",
  paddingInline: "15px",
  width: spacing.full,
};
const summaryStatisticContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  textAlign: "center",
};
const summaryStatisticLabelStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  whiteSpace: "normal",
  [cssMediaQueryMinWidthTablet]: {
    whiteSpace: "nowrap",
  },
};

export type TheWordScoreProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  actions?: JSX.Element[];
  shareButtonProps?: ButtonProps;
  size?: "m" | "l";
  summary: TheWordGame["summary"];
};

interface TheWordAnswerScoreProps {
  guess: TheWordGame["summary"]["matrix"][0];
  index: number;
  size: TheWordScoreProps["size"];
}

const TheWordAnswerScore = ({
  guess,
  index,
  size,
}: TheWordAnswerScoreProps) => {
  const isLargeDesktop = useDynamicViewport({ minimumWidth: 1500 });

  return (
    <div css={guessContainerStyle}>
      {guess.map((letter, letterIdx) => (
        <div
          key={`guess${index}_${letterIdx}`}
          css={[
            iconContainerStyle({
              size:
                size === "l"
                  ? isLargeDesktop
                    ? 48
                    : 24
                  : isLargeDesktop
                    ? 32
                    : 16,
            }),
            letter === "in_word_in_position"
              ? iconContainerSuccessStyle
              : letter === "in_word"
                ? iconContainerPartialStyle
                : iconContainerNeverStyle,
          ]}
        />
      ))}
    </div>
  );
};

export const TheWordScore = ({
  actions,
  asChild = false,
  children,
  size = "m",
  summary,
  ...props
}: TheWordScoreProps) => {
  const format = useFormatter();
  const t = useTranslations();

  const DeterminedElement = determineElementFromAsChild({
    asChild,
    hallowElement,
  });

  const DeterminedChildren = determineChildrenInject({
    afterChildrenInject: [
      <Text
        key="text"
        type={size === "l" ? "headline" : "title"}
        size={size === "l" ? "m" : "l"}
      >
        {t("trivia_your_score")}
      </Text>,
      <div key="scoresContainer" css={guessesContainerStyle}>
        {summary.matrix.map((guess, index) => (
          <TheWordAnswerScore
            key={index}
            guess={guess}
            index={index}
            size={size}
          />
        ))}
      </div>,
      <div key="summaryContainer" css={summaryContainerStyle}>
        {[
          {
            label: t("web_the_word_summary_win_percentage"),
            value: format.number(summary.win_percentage / 100, {
              style: "percent",
            }),
          },
          {
            label: t("trivia_current_streak"),
            value: format.number(summary.streak),
          },
          {
            label: t("trivia_max_streak"),
            value: format.number(summary.longest_streak),
          },
        ].map((statistic) => (
          <div key={statistic.label} css={summaryStatisticContainerStyle}>
            <Text
              type={size === "l" ? "headline" : "title"}
              size={size === "l" ? "m" : "l"}
            >
              {statistic.value}
            </Text>
            <Text type="detail" size="xl" css={summaryStatisticLabelStyle}>
              {statistic.label}
            </Text>
          </div>
        ))}
      </div>,
      actions?.length > 0 ? (
        <div key="actions" css={actionsStyle}>
          {actions}
        </div>
      ) : null,
    ],
    children: children as JSX.Element,
  });

  return (
    <DeterminedElement
      css={containerStyle}
      {...props}
      aria-description={t("the_word_accessibility_stats", {
        "0": summary.win_percentage,
        "1": summary.streak,
        "2": summary.longest_streak,
      })}
    >
      {DeterminedChildren}
    </DeterminedElement>
  );
};
